// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { useTheme } from '@/components/theme-provider';
import {
  auroraSettings,
  curtainSources,
  FIELD_SHADER,
  FINISH_SHADER,
  hslTokenToRgb,
  REST_TIME,
  VERTEX_SHADER,
} from '@/lib/aurora';
import { cn } from '@/lib/utils';

/** Pixel budgets: the canvas at device resolution (capped), the shaded field at a fraction of it. */
const CANVAS_BUDGET = 12e6;
const FIELD_BUDGET = 2e6;

function compile(gl: WebGLRenderingContext, fragment: string): WebGLProgram | null {
  const program = gl.createProgram();
  if (!program) return null;
  for (const [type, source] of [
    [gl.VERTEX_SHADER, VERTEX_SHADER],
    [gl.FRAGMENT_SHADER, fragment],
  ] as const) {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    gl.attachShader(program, shader);
    gl.deleteShader(shader);
  }
  gl.bindAttribLocation(program, 0, 'a_position');
  gl.linkProgram(program);
  return gl.getProgramParameter(program, gl.LINK_STATUS) ? program : null;
}

/**
 * The marketing site's northern lights, drawn behind a standalone page. It is
 * decorative: without WebGL it draws nothing and the page background shows;
 * under `prefers-reduced-motion` it paints one still frame; it stops while the
 * tab is hidden.
 */
export function Aurora({ className }: { className?: string }): React.ReactElement {
  const ref = React.useRef<HTMLCanvasElement>(null);
  const { resolvedTheme } = useTheme();
  const [contextEpoch, setContextEpoch] = React.useState(0);

  React.useEffect(() => {
    const canvas = ref.current;
    const gl = canvas?.getContext('webgl', { antialias: false, alpha: false }) ?? null;
    if (!canvas || !gl || gl.isContextLost()) return;

    const onLost = (e: Event) => e.preventDefault();
    const onRestored = () => setContextEpoch((n) => n + 1);
    canvas.addEventListener('webglcontextlost', onLost);
    canvas.addEventListener('webglcontextrestored', onRestored);

    const field = compile(gl, FIELD_SHADER);
    const finish = compile(gl, FINISH_SHADER);
    const texture = gl.createTexture();
    const framebuffer = gl.createFramebuffer();
    const buffer = gl.createBuffer();
    const release = () => {
      canvas.removeEventListener('webglcontextlost', onLost);
      canvas.removeEventListener('webglcontextrestored', onRestored);
      gl.deleteProgram(field);
      gl.deleteProgram(finish);
      gl.deleteTexture(texture);
      gl.deleteFramebuffer(framebuffer);
      gl.deleteBuffer(buffer);
    };
    if (!field || !finish) return release;

    const token = getComputedStyle(document.documentElement).getPropertyValue('--background');
    const s = auroraSettings(resolvedTheme, hslTokenToRgb(token));

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    gl.useProgram(finish);
    gl.uniform1i(gl.getUniformLocation(finish, 'u_field'), 0);
    gl.uniform2f(gl.getUniformLocation(finish, 'u_grain'), s.grain, s.seed);
    const finishSize = gl.getUniformLocation(finish, 'u_size');

    gl.useProgram(field);
    gl.uniform3fv(gl.getUniformLocation(field, 'u_colors'), s.colors.flat());
    gl.uniform3fv(gl.getUniformLocation(field, 'u_accent'), s.accents.flat());
    gl.uniform3f(gl.getUniformLocation(field, 'u_shape'), s.intensity, s.seed, 0);
    gl.uniform3f(gl.getUniformLocation(field, 'u_post'), s.contrast, s.brightness, s.saturation);
    const u = {
      resolution: gl.getUniformLocation(field, 'u_resolution'),
      time: gl.getUniformLocation(field, 'u_time'),
      spin: gl.getUniformLocation(field, 'u_spin'),
      sources: gl.getUniformLocation(field, 'u_sources'),
    };

    let fieldW = 0;
    let fieldH = 0;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      const w = Math.max(1, rect.width * dpr);
      const h = Math.max(1, rect.height * dpr);
      const k = Math.min(1, Math.sqrt(CANVAS_BUDGET / (w * h)));
      canvas.width = Math.max(1, Math.round(w * k));
      canvas.height = Math.max(1, Math.round(h * k));
      const f = Math.min(1, Math.sqrt(FIELD_BUDGET / (canvas.width * canvas.height)));
      fieldW = Math.max(1, Math.round(canvas.width * f));
      fieldH = Math.max(1, Math.round(canvas.height * f));
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, fieldW, fieldH, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    };

    const draw = (t: number) => {
      gl.useProgram(field);
      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
      gl.viewport(0, 0, fieldW, fieldH);
      gl.uniform2f(u.resolution, fieldW, fieldH);
      gl.uniform1f(u.time, t * s.timeScale);
      gl.uniform1f(u.spin, t * s.timeScale * 0.3);
      gl.uniform4fv(u.sources, curtainSources(t, canvas.width / canvas.height));
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      gl.useProgram(finish);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(finishSize, canvas.width, canvas.height);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      frame = 0;
      draw((now - start) / 1000);
      if (!still && document.visibilityState === 'visible') frame = requestAnimationFrame(tick);
    };
    const paint = () => {
      if (still) draw(REST_TIME);
      else if (!frame && document.visibilityState === 'visible')
        frame = requestAnimationFrame(tick);
    };

    const observer = new ResizeObserver(() => {
      resize();
      paint();
    });
    observer.observe(canvas);
    document.addEventListener('visibilitychange', paint);
    resize();
    paint();

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
      document.removeEventListener('visibilitychange', paint);
      release();
    };
  }, [resolvedTheme, contextEpoch]);

  return (
    <canvas
      ref={ref}
      aria-hidden
      data-testid="aurora"
      className={cn('pointer-events-none block h-full w-full', className)}
    />
  );
}
